/**
 * @fileoverview Operational public-safety artwork catalog.
 * @module ui-artwork/catalog/operational
 *
 * Responsibilities: Register NFPA/NAPSG-style hazards, access, water, and
 *   building-system symbols with controlled palette metadata.
 * Dependencies: ArtworkCatalogEntry.
 * Architecture: Static package catalog data; symbols render from local SVG.
 * Notes: Hazard colors reflect their applicable public-safety conventions.
 */
import type { ArtworkCatalogEntry } from '../types';

const controlled: ArtworkCatalogEntry['allowedHazardColors'] = [
  { hex: '#dc2626', label: 'Flammable gas', standard: 'DOT 2.1' },
  { hex: '#f97316', label: 'Warning border', standard: 'NAPSG' },
];
const nfpa704: ArtworkCatalogEntry['allowedHazardColors'] = [
  { hex: '#dc2626', label: 'Flammability quadrant', standard: 'NFPA 704 red' },
  { hex: '#2563eb', label: 'Health quadrant', standard: 'NFPA 704 blue' },
  { hex: '#facc15', label: 'Instability quadrant', standard: 'NFPA 704 yellow' },
  { hex: '#ffffff', label: 'Special hazard quadrant', standard: 'NFPA 704 white' },
];
const make = (id: string, label: string, category: ArtworkCatalogEntry['category'], keywords: string[], policy: ArtworkCatalogEntry['colorPolicy'] = 'customizable'): ArtworkCatalogEntry => ({
  id: `art:ops/${id}`,
  label,
  category,
  style: 'operational',
  keywords: [label.toLowerCase(), ...keywords],
  colorPolicy: policy,
  defaultColor: policy === 'controlled-hazard' ? '#dc2626' : '#dc2626',
  ...(policy === 'controlled-hazard' ? { allowedHazardColors: id === 'nfpa-704-diamond' ? nfpa704 : controlled } : {}),
  source: 'napsg-operational',
  attribution: 'NAPSG-style authored SVG (style reference: NAPSG Foundation and NFPA 170)',
});

export const OPERATIONAL_ARTWORK: ArtworkCatalogEntry[] = [
  make('hazard-propane-tank', 'Propane storage tank', 'hazard', ['propane', 'lpg', 'flammable gas'], 'controlled-hazard'),
  make('hazard-aboveground-tank', 'Aboveground storage tank', 'hazard', ['ast', 'aboveground tank', 'fuel'], 'controlled-hazard'),
  make('hazard-underground-tank', 'Underground storage tank', 'hazard', ['ust', 'underground tank', 'fuel'], 'controlled-hazard'),
  make('nfpa-704-diamond', 'NFPA 704 diamond', 'hazard', ['nfpa', 'diamond', 'health', 'flammability'], 'controlled-hazard'),
  make('hazmat-placard', 'Hazmat placard', 'hazard', ['hazmat', 'placard', 'dot'], 'controlled-hazard'),
  {
    ...make('water-hydrant', 'Fire hydrant', 'equipment', ['hydrant', 'nfpa 291', 'fire flow'], 'controlled-hazard'),
    legacyAliases: ['fs-fire-hydrant', 'FsFireHydrant'],
    allowedHazardColors: [
      { hex: '#2563eb', label: '1500+ GPM', standard: 'NFPA 291 blue' },
      { hex: '#16a34a', label: '1000–1499 GPM', standard: 'NFPA 291 green' },
      { hex: '#f97316', label: '500–999 GPM', standard: 'NFPA 291 orange' },
      { hex: '#dc2626', label: 'Below 500 GPM', standard: 'NFPA 291 red' },
    ],
    defaultColor: '#2563eb',
  },
  make('water-fdc', 'Fire department connection', 'equipment', ['fdc', 'siamese', 'water']),
  make('water-piv', 'Post indicator valve', 'equipment', ['piv', 'os&y', 'valve']),
  make('water-draft-site', 'Static water source', 'equipment', ['dry hydrant', 'cistern', 'water source']),
  make('access-knox-box', 'Rapid entry key box', 'equipment', ['knox box', 'access', 'rapid entry']),
  make('access-fire-lane', 'Fire apparatus access lane', 'navigation', ['fire lane', 'staging', 'access']),
  make('util-gas-shutoff', 'Natural gas shutoff', 'hazard', ['gas shutoff', 'utility', 'valve'], 'controlled-hazard'),
  make('util-electric-shutoff', 'Electrical shutoff', 'hazard', ['electric shutoff', 'disconnect', 'utility'], 'controlled-hazard'),
  make('sys-facp', 'Fire alarm control panel', 'equipment', ['facp', 'fire alarm', 'panel'], 'controlled-hazard'),
  make('sys-sprinkler-riser', 'Sprinkler riser', 'equipment', ['sprinkler', 'riser', 'system']),
  make('sys-standpipe', 'Standpipe connection', 'equipment', ['standpipe', 'fire protection']),
  make('sys-smoke-detector', 'Smoke detector', 'equipment', ['smoke', 'heat detector']),
  make('sys-hood-suppression', 'Kitchen hood suppression', 'equipment', ['hood', 'suppression', 'ul 300']),
  make('hazard-truss-roof', 'Lightweight truss roof hazard', 'hazard', ['truss', 'roof', 'collapse'], 'controlled-hazard'),
  {
    ...make('equip-aed', 'Automated external defibrillator', 'equipment', ['aed', 'defibrillator', 'medical']),
    legacyAliases: ['fs-aed', 'FsAed'],
  },
];
